import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';

export const BasicTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'buttonText',
  'showName',
  'showEmail',
  'requireName',
  'requireEmail',
  'requireAcceptMarketing',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'primaryColor',
  'buttonColor',
  'cardBackgroundColor',
  'cardHeaderColor',
  'cardTextColor',
  'cardFieldTextColor',
  'cardFieldBorderColor',
  'buttonTextColor',
  'cardTextAlign',
  'cardHeaderAlign',
  'logoAlign',
] as const;

export const BasicTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof BasicTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Paper
        elevation={3}
        sx={{
          px: 7,
          pt: 2,
          pb: 5,
          mt: 3.5,
          maxWidth: 'md',
          width: '100%',
          borderRadius: 2,
          backgroundColor: data.cardBackgroundColor || 'background.paper',
        }}
      >
        {/* Header */}
        {data.header && (
          <Typography
            variant="h5"
            component="h1"
            sx={{
              textAlign: data.cardHeaderAlign || 'center',
              mb: 3,
              fontWeight: 700,
              color: data.cardHeaderColor || 'text.primary',
            }}
          >
            {data.header}
          </Typography>
        )}

        {/* Content */}
        {data.content && (
          <Typography
            variant="body1"
            sx={{
              textAlign: data.cardTextAlign || 'center',
              mb: 4,
              color: data.cardTextColor || 'text.secondary',
              whiteSpace: 'pre-wrap',
            }}
            fontSize={14}
          >
            {data.content}
          </Typography>
        )}

        {data.logoUrl && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: data.logoAlign || 'center',
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={data.logoUrl}
              alt={t('logo', 'Logo')}
              sx={{
                maxHeight: 116,
                maxWidth: '100%',
                height: 'auto',
                width: 'auto',
              }}
            />
          </Box>
        )}

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* Email Field */}
          {data.showEmail && (
            <StyledTextField
              data={data}
              type="email"
              label={t('emailAddress', 'Email address')}
              placeholder={t('emailAddressPlaceholder', 'Email address')}
              value={formData.email || ''}
              onChange={handleInputChange('email')}
              fullWidth
              variant="outlined"
              required={data.requireEmail}
            />
          )}

          {/* Name Fields */}
          {data.showName && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <StyledTextField
                  data={data}
                  type="text"
                  label={t('firstName', 'First name')}
                  placeholder={t('firstNamePlaceholder', 'First name')}
                  value={formData.firstName || ''}
                  onChange={handleInputChange('firstName')}
                  fullWidth
                  variant="outlined"
                  required={data.requireName}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <StyledTextField
                  data={data}
                  type="text"
                  label={t('lastName', 'Last name')}
                  placeholder={t('lastNamePlaceholder', 'Last name')}
                  value={formData.lastName || ''}
                  onChange={handleInputChange('lastName')}
                  fullWidth
                  variant="outlined"
                  required={data.requireName}
                />
              </Grid>
            </Grid>
          )}

          {(data.termsAndConditionsUrl || data.requireAcceptMarketing) && (
            <Box display="flex" flexDirection="column">
              {data.termsAndConditionsUrl && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedTerms || false}
                      onChange={handleInputChange('acceptedTerms')}
                      sx={{
                        color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                        '&.Mui-checked': {
                          color: data.cardFieldBorderColor || data.primaryColor || 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: data.cardTextColor || 'text.primary' }}
                    >
                      {t('acceptTerms', 'I accept the')}{' '}
                      <Link
                        href={data.termsAndConditionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: data.primaryColor || 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('termsConditions', 'Terms & Conditions')}
                      </Link>
                      {data.privacyPolicyUrl && (
                        <>
                          {' '}
                          {t('and', 'and')}{' '}
                          <Link
                            href={data.privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: data.primaryColor || 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {t('privacyPolicy', 'Privacy Policy')}
                          </Link>
                        </>
                      )}
                      .
                    </Typography>
                  }
                  sx={{ alignItems: 'center' }}
                />
              )}

              {data.requireAcceptMarketing && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedMarketing || false}
                      onChange={handleInputChange('acceptedMarketing')}
                      sx={{
                        color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                        '&.Mui-checked': {
                          color: data.cardFieldBorderColor || data.primaryColor || 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: data.cardTextColor || 'text.primary' }}
                    >
                      {t(
                        'acceptMarketing',
                        'I would like to receive marketing communications and promotional offers',
                      )}
                    </Typography>
                  }
                  sx={{ alignItems: 'center' }}
                />
              )}
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor: data.buttonColor || 'primary.main',
              color: data.buttonTextColor || 'white',
              '&:hover': {
                backgroundColor: data.buttonColor || 'primary.dark',
              },
              '&:disabled': {
                opacity: 0.5,
                backgroundColor: data.buttonColor || 'primary.main',
                color: data.buttonTextColor || 'white',
              },
            }}
          >
            {isSubmitting
              ? t('connecting', 'Connecting...')
              : data.buttonText || t('connectToInternet', 'Connect to Internet')}
          </Button>
        </Box>
      </Paper>
    </CaptivePortalContainer>
  );
};

function StyledTextField(
  props: ComponentProps<typeof TextField> & {
    data: CaptivePortalTemplateProps<(typeof BasicTemplateDataKeys)[number]>['data'];
  },
) {
  const { data } = props;
  return (
    <TextField
      {...props}
      sx={{
        '& .MuiInputLabel-root': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiInputBase-input': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: data.buttonColor || 'primary.main',
          },
        },
      }}
    />
  );
}
